import React, { FC, memo, useCallback, useState } from 'react'
import { SportsEvent } from 'schema-dts'
import { useRouter } from 'next/router'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import widget from '@sport1/sport1-components/build/widgets/Universal/Liveticker'
import withWidgetConfig from '@sport1/sport1-components/build/widgets/config/component'
import { LabelProps, LivetickerWidgetProps, TagProps } from '@sport1/types/web'
import { MatchClientModel, PlayerClientModel } from '@sport1/types/sportData'
import { BettingActionProperties } from '@sport1/bets-service-client'
import LinkedData from '@/components/LinkedData'
import getSportsEventsByMatches from '@/components/LinkedData/getSportsEventsByMatches'
import {
    createGameplanHref,
    createPlayerInfoHref,
    createTeamHref,
} from '@/utils/navigation/Navigator'
import { handleBettingAction } from '@/helpers/bettingHelper'

import { useTracking } from '@/utils/tracking/TrackingProvider'
import useBettingTracking from '@/hooks/useBettingTracking'

type LivetickerProps = LivetickerWidgetProps & {
    tag?: TagProps | LabelProps
}

const LivetickerWidget = withWidgetConfig(widget)

const Liveticker: FC<LivetickerProps> = ({ matchId, sportIdentifier, tag, ...rest }) => {
    const router = useRouter()
    const { trackIvwInfo, trackIvwView } = useTracking()
    const [linkedData, setLinkedData] = useState<SportsEvent[]>([])
    // TODO: we can not update bet-service-client because of sport1-components, maybe using S1_WIDGETS like in the App?
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { trackBetImpression, trackBetClick } = useBettingTracking('quotenbanner_liveticker')

    const createPlayerHref = useCallback(
        (player: PlayerClientModel) => createPlayerInfoHref(player),
        []
    )

    const createShowGameplanHref = useCallback(() => createGameplanHref({ tag }), [tag])

    const getMatchData = useCallback(
        (data: MatchClientModel) =>
            setLinkedData(getSportsEventsByMatches({ matches: [data], tag, sportIdentifier })),
        [sportIdentifier, tag]
    )

    const bettingActionHandler = useCallback(
        (bettingActionProperties: BettingActionProperties) => {
            handleBettingAction({
                bettingActionProperties,
                ignoreMatchStatus: true,
                trackBetImpression,
                trackBetClick,
            })
        },
        [trackBetClick, trackBetImpression]
    )

    return (
        <NonFlexingContainer paddingY={['spacing-6', 'spacing-6', 'spacing-9', 'spacing-none']}>
            <LivetickerWidget
                createTeamHref={createTeamHref}
                createPlayerHref={createPlayerHref}
                createShowGameplanHref={createShowGameplanHref}
                getMatchData={getMatchData}
                sportIdentifier={sportIdentifier}
                bettingActionHandler={bettingActionHandler}
                bettingWidgetTrackingIdentifier="quotenbanner_liveticker"
                matchId={router.query.matchId || matchId}
                startTabIndex={router.query.initial ? parseInt(router.query.initial as string) : 0}
                onTabChange={(data: { label: string }) => {
                    trackIvwInfo({ ivw_co: data.label.toLowerCase() })
                    trackIvwView()
                }}
                {...rest}
            />
            <LinkedData linkedData={linkedData} />
        </NonFlexingContainer>
    )
}

export default memo(Liveticker)
